import { intranetText, type IntranetLinkCategory } from '@loot/shared';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  BookOpenCheck,
  Cake,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Link2,
  Megaphone,
  Newspaper,
  Plus,
  RefreshCw,
} from 'lucide-react';
import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { Markdown } from '../../components/Markdown.tsx';
import { Modal } from '../../components/Modal.tsx';
import { Button } from '../../components/ui.tsx';
import { PersonAvatar } from '../people/common.tsx';
import { dateBox, type IntranetHome, type IntranetPost, imageUrl, isInternal } from './common.ts';

/** O conteúdo da intranet no Início (ADR 0077): uma consulta só, o cache é o mesmo do Início. */
export function useIntranetHome() {
  return useQuery({
    queryKey: ['intranet', 'home'],
    queryFn: () => api.get<IntranetHome>('/intranet/home'),
    retry: 1,
  });
}

type HomeQuery = UseQueryResult<IntranetHome>;

interface Birthday {
  personId: string;
  name: string;
  day: number;
  month: number;
  departmentName: string | null;
  photoId: string | null;
}

/**
 * Aniversariantes do ano todo (ADR 0069, decisão de 27/10: todos os logados veem), numa consulta só:
 * a navegação entre meses não pergunta de novo. Só nome, foto, dia e área; nunca o ano.
 */
export function useBirthdays() {
  return useQuery({
    queryKey: ['people', 'birthdays', 'intranet', 'year'],
    queryFn: () => api.get<{ items: Birthday[] }>('/people/birthdays/intranet'),
    retry: 1,
  });
}

/** Painel de um bloco do Início: ícone, título e a ação à direita. */
export function Block({
  icon: Icon,
  title,
  action,
  className = '',
  children,
}: {
  icon: typeof Megaphone;
  title: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={`flex min-w-0 flex-col gap-3 rounded-lg border border-rule bg-sheet p-4 shadow-sm sm:p-5 ${className}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon size={18} strokeWidth={1.75} aria-hidden className="text-muted" />
        <h2 className="text-base font-semibold">{title}</h2>
        {action && <div className="ml-auto flex items-center gap-1">{action}</div>}
      </div>
      {children}
    </section>
  );
}

/** Enquanto carrega: linhas cinzas, sem texto nem número. */
function Skeleton({ lines = 3 }: { lines?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t('common.loading')} className="flex flex-col gap-3 py-1">
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <div className="h-3 w-3/5 animate-pulse rounded bg-rule" />
          <div className="h-2.5 w-4/5 animate-pulse rounded bg-rule" />
        </div>
      ))}
    </div>
  );
}

/** Falha da consulta: o bloco fica, diz o que não carregou e oferece tentar de novo. */
function Failure({ what, onRetry }: { what: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div role="alert" className="flex flex-col items-start gap-2 text-sm">
      <p className="flex items-start gap-2">
        <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0 text-danger" />
        {t('intranet.home.failed', { what })}
      </p>
      <Button variant="secondary" onClick={onRetry} className="min-h-9 px-3">
        <RefreshCw size={14} aria-hidden />
        {t('intranet.home.retry')}
      </Button>
    </div>
  );
}

/** Sem conteúdo: uma frase simples; quem publica na intranet recebe também o atalho para publicar. */
function Empty({ text, publish }: { text: string; publish?: { to: string; label: string } }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  return (
    <div className="flex flex-col gap-2 text-sm text-muted">
      <p>{text}</p>
      {publish && can(me, 'intranet.content.manage') && (
        <p className="rounded-md border border-dashed border-control-border bg-paper px-3 py-2 text-ink">
          {t('intranet.home.publishHint')}{' '}
          <Link
            to={publish.to}
            className="inline-flex items-center gap-1 font-medium text-brand-text hover:underline"
          >
            <Plus size={14} aria-hidden />
            {publish.label}
          </Link>
        </p>
      )}
    </div>
  );
}

/** Corpo do bloco pelo estado da consulta: carregando, falha, vazio ou o conteúdo. */
function byState<T>(
  query: UseQueryResult<unknown>,
  items: T[] | undefined,
  states: { what: string; empty: ReactNode; content: (items: T[]) => ReactNode },
): ReactNode {
  if (query.isPending) return <Skeleton />;
  if (query.isError) return <Failure what={states.what} onRetry={() => void query.refetch()} />;
  if (!items?.length) return states.empty;
  return states.content(items);
}

/**
 * Imagem da publicação (novo personagem, comunicado que é uma imagem...), sempre inteira, sem cortar:
 * miniatura ao lado do comunicado, quadro 16:9 na novidade e grande no Ler mais, onde fica legível.
 */
const IMAGE_SIZE = {
  thumb: 'h-[66px] w-[88px] shrink-0 sm:h-[90px] sm:w-[136px]',
  card: 'aspect-video w-full',
  large: 'max-h-[70vh] w-full',
} as const;

function PostImage({ post, size }: { post: IntranetPost; size: keyof typeof IMAGE_SIZE }) {
  const { t } = useTranslation();
  if (!post.imageId) return null;
  return (
    <img
      src={imageUrl(post.imageId)}
      alt={post.imageDescription ?? t('intranet.image.alt')}
      loading="lazy"
      className={`rounded-md border border-rule bg-paper object-contain ${IMAGE_SIZE[size]}`}
    />
  );
}

/** Quando é o evento: dia, hora (com o fuso escrito) e local. */
function eventWhen(p: IntranetPost, lang: string, t: TFunction): string {
  if (!p.startsAt) return '';
  const start = new Date(p.startsAt);
  const day = new Intl.DateTimeFormat(lang, { dateStyle: 'full' }).format(start);
  const time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' });
  const zone = new Intl.DateTimeFormat(lang, { timeZoneName: 'short' })
    .formatToParts(start)
    .find((x) => x.type === 'timeZoneName')?.value;
  const hours = p.allDay
    ? t('intranet.home.allDay')
    : `${time.format(start)}${p.endsAt ? ` – ${time.format(new Date(p.endsAt))}` : ''}${zone ? ` (${zone})` : ''}`;
  return [day, hours, p.location].filter(Boolean).join(' · ');
}

/** Abre o texto completo de uma publicação numa janela por cima do Início. */
function Reader({ post, onClose }: { post: IntranetPost | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const body = post ? intranetText(post.body, lang) : '';
  return (
    <Modal
      open={!!post}
      onClose={onClose}
      size="lg"
      title={post ? intranetText(post.title, lang) : ''}
      description={post?.ownerArea ?? undefined}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {post && (
        <div className="flex flex-col gap-3 text-sm">
          {post.kind === 'event' && (
            <p className="font-medium">
              {t(`intranet.eventKinds.${post.eventKind ?? 'event'}`)} · {eventWhen(post, lang, t)}
            </p>
          )}
          <PostImage post={post} size="large" />
          {intranetText(post.summary, lang) && (
            <p className="font-medium">{intranetText(post.summary, lang)}</p>
          )}
          {body && <Markdown>{body}</Markdown>}
          {post.documentUrl && (
            <DocLink
              url={post.documentUrl}
              label={t(
                post.kind === 'event' ? 'intranet.home.openLink' : 'intranet.home.openDocument',
              )}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function DocLink({ url, label }: { url: string; label: string }) {
  return isInternal(url) ? (
    <Link to={url} className="text-sm text-brand-text hover:underline">
      {label}
    </Link>
  ) : (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-sm text-brand-text hover:underline"
    >
      {label}
      <ExternalLink size={14} aria-hidden />
    </a>
  );
}

const PUBLISH_POSTS = '/hr/intranet';

/** Coluna principal: comunicados importantes e novidades da LOOT. */
export function MainBlocks({ home }: { home: HomeQuery }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [reading, setReading] = useState<IntranetPost | null>(null);
  const day = new Intl.DateTimeFormat(lang, { dateStyle: 'medium' });
  const readMore = (p: IntranetPost, key = 'intranet.home.readMore') =>
    (intranetText(p.body, lang) || p.documentUrl || p.imageId) && (
      <button
        type="button"
        onClick={() => setReading(p)}
        className="self-start text-sm font-medium text-brand-text hover:underline"
      >
        {t(key)}
      </button>
    );
  const news = home.data?.news;
  return (
    <>
      <Block
        icon={Megaphone}
        title={t('intranet.home.announcements')}
        className="order-1 lg:order-none"
      >
        {byState(home, home.data?.announcements, {
          what: t('intranet.home.whatAnnouncements'),
          empty: (
            <Empty
              text={t('intranet.home.empty.announcements')}
              publish={{ to: PUBLISH_POSTS, label: t('intranet.home.publish.announcement') }}
            />
          ),
          content: (items) => (
            // Um comunicado por linha; o urgente com a palavra e uma faixa à esquerda, nunca só a cor.
            <ul className="flex flex-col divide-y divide-rule">
              {items.map((p) => (
                <li
                  key={p.id}
                  className="flex items-start gap-3 py-3 first:pt-0 last:pb-0 sm:gap-4"
                >
                  <PostImage post={p} size="thumb" />
                  <div
                    className={`flex min-w-0 flex-col gap-1 ${p.urgent ? 'border-l-[3px] border-danger pl-3' : ''}`}
                  >
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                      {p.urgent && (
                        <span className="rounded-full border border-danger px-2 font-medium text-danger">
                          {t('intranet.fields.urgent')}
                        </span>
                      )}
                      {[
                        p.ownerArea,
                        day.format(new Date(p.displayFrom)),
                        p.displayUntil &&
                          t('intranet.home.until', { date: day.format(new Date(p.displayUntil)) }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <h3 className="text-sm font-semibold sm:text-base">
                      {intranetText(p.title, lang)}
                    </h3>
                    {intranetText(p.summary, lang) && (
                      <p className="line-clamp-2 text-sm">{intranetText(p.summary, lang)}</p>
                    )}
                    {readMore(p)}
                  </div>
                </li>
              ))}
            </ul>
          ),
        })}
      </Block>
      <Block icon={Newspaper} title={t('intranet.home.news')} className="order-3 lg:order-none">
        {byState(home, news, {
          what: t('intranet.home.whatNews'),
          empty: (
            <Empty
              text={t('intranet.home.empty.news')}
              publish={{ to: PUBLISH_POSTS, label: t('intranet.home.publish.news') }}
            />
          ),
          content: ([first, ...rest]) => (
            <div className="flex flex-col gap-4">
              {/* A notícia mais recente em destaque, com a imagem; as demais, compactas. */}
              <div
                className={`grid gap-3 ${first!.imageId ? 'md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:items-start' : ''}`}
              >
                <PostImage post={first!} size="card" />
                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-xs text-muted">
                    {[first!.ownerArea, day.format(new Date(first!.displayFrom))]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <h3 className="text-base font-semibold">{intranetText(first!.title, lang)}</h3>
                  {intranetText(first!.summary, lang) && (
                    <p className="line-clamp-3 text-sm">{intranetText(first!.summary, lang)}</p>
                  )}
                  {readMore(first!, 'intranet.home.readNews')}
                </div>
              </div>
              {rest.length > 0 && (
                <ul className="grid gap-x-5 border-t border-rule pt-3 sm:grid-cols-2 xl:grid-cols-3">
                  {rest.map((p) => (
                    <li key={p.id} className="flex min-w-0 flex-col gap-1 py-2">
                      <span className="text-xs text-muted">
                        {[p.ownerArea, day.format(new Date(p.displayFrom))]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      <h3 className="text-sm font-semibold">{intranetText(p.title, lang)}</h3>
                      {intranetText(p.summary, lang) && (
                        <p className="line-clamp-2 text-sm">{intranetText(p.summary, lang)}</p>
                      )}
                      {readMore(p)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ),
        })}
      </Block>
      <Reader post={reading} onClose={() => setReading(null)} />
    </>
  );
}

/** Coluna de apoio: aniversariantes, agenda e, quando houver, políticas e normativas. */
export function SideBlocks({ home }: { home: HomeQuery }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [reading, setReading] = useState<IntranetPost | null>(null);
  const time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' });
  const day = new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeZone: 'UTC' });
  const policies = home.data?.policies ?? [];
  return (
    <>
      <BirthdaysBlock />
      <Block
        icon={CalendarDays}
        title={t('intranet.home.events')}
        className="order-4 lg:order-none"
      >
        {byState(home, home.data?.events, {
          what: t('intranet.home.whatEvents'),
          empty: (
            <Empty
              text={t('intranet.home.empty.events')}
              publish={{ to: PUBLISH_POSTS, label: t('intranet.home.publish.event') }}
            />
          ),
          content: (items) => (
            <ul className="flex flex-col divide-y divide-rule">
              {items.map((p) => {
                const start = new Date(p.startsAt!);
                const box = dateBox(start, lang);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setReading(p)}
                      className="flex w-full items-start gap-3 py-2 text-left hover:[&_b]:underline"
                    >
                      <span className="flex w-12 shrink-0 flex-col items-center rounded-md border border-rule py-0.5 tabular-nums">
                        <span className="text-base leading-5 font-semibold">{box.day}</span>
                        <span className="text-[11px] text-muted uppercase">{box.month}</span>
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <b className="text-sm font-medium">{intranetText(p.title, lang)}</b>
                        <span className="text-xs text-muted">
                          {[
                            t(`intranet.eventKinds.${p.eventKind ?? 'event'}`),
                            p.allDay ? t('intranet.home.allDay') : time.format(start),
                            p.location,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ),
        })}
      </Block>
      {/* Políticas e normativas: só quando há alguma publicada (a falha já aparece nos outros blocos). */}
      {policies.length > 0 && (
        <Block
          icon={BookOpenCheck}
          title={t('intranet.home.policies')}
          className="order-5 lg:order-none"
        >
          <ul className="flex flex-col gap-3">
            {policies.map((p) => (
              <li key={p.id} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => setReading(p)}
                  className="text-left text-sm font-medium hover:underline"
                >
                  {intranetText(p.title, lang)}
                </button>
                <span className="text-xs text-muted">
                  {[
                    p.version && t('intranet.home.version', { version: p.version }),
                    p.effectiveFrom &&
                      t('intranet.home.effective', {
                        date: day.format(new Date(`${p.effectiveFrom}T00:00:00Z`)),
                      }),
                    p.ownerArea,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </Block>
      )}
      <Reader post={reading} onClose={() => setReading(null)} />
    </>
  );
}

/** Quantos aniversariantes o bloco mostra; o mês inteiro abre numa janela. */
const BIRTHDAYS_SHOWN = 5;

/**
 * Aniversariantes do mês, com navegação entre meses. No mês atual, a lista começa nos de hoje e nos
 * próximos, sempre em ordem de dia; os de hoje ficam destacados. Sem ninguém no ano, o bloco só diz
 * que não há aniversários para divulgar: a lista não distingue falta de datas de quem pediu para não
 * aparecer, e não deve revelar isso. Quem cadastra dados pessoais vê de onde a lista vem.
 */
function BirthdaysBlock() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const birthdays = useBirthdays();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [full, setFull] = useState(false);
  const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(
    new Date(now.getFullYear(), month - 1, 1),
  );
  const all = birthdays.data?.items ?? [];
  const list = all
    .filter((b) => b.month === month)
    .sort((a, b) => a.day - b.day || a.name.localeCompare(b.name));
  const isToday = (b: Birthday) => b.month === now.getMonth() + 1 && b.day === now.getDate();
  const from =
    month === now.getMonth() + 1
      ? Math.max(
          0,
          Math.min(
            Math.max(
              0,
              list.findIndex((b) => b.day >= now.getDate()),
            ),
            list.length - BIRTHDAYS_SHOWN,
          ),
        )
      : 0;
  const shown = list.slice(from, from + BIRTHDAYS_SHOWN);
  const step = (delta: number) => setMonth((m) => ((m - 1 + delta + 12) % 12) + 1);
  const nav = (
    <span className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => step(-1)}
        aria-label={t('intranet.home.prevMonth')}
        className="flex size-8 items-center justify-center rounded-md border border-rule text-muted hover:border-control-border hover:text-ink"
      >
        <ChevronLeft size={16} aria-hidden />
      </button>
      <span className="min-w-20 text-center text-sm font-medium" aria-live="polite">
        {monthName}
      </span>
      <button
        type="button"
        onClick={() => step(1)}
        aria-label={t('intranet.home.nextMonth')}
        className="flex size-8 items-center justify-center rounded-md border border-rule text-muted hover:border-control-border hover:text-ink"
      >
        <ChevronRight size={16} aria-hidden />
      </button>
    </span>
  );
  const row = (b: Birthday) => (
    <li
      key={b.personId}
      className={`flex items-center gap-3 py-1.5 ${isToday(b) ? '-mx-2 rounded-md bg-brand-soft px-2' : ''}`}
    >
      <PersonAvatar name={b.name} photoId={b.photoId} />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium">{b.name}</span>
        <span className="text-xs text-muted">
          {[t('intranet.home.dayOf', { day: b.day, month: monthName }), b.departmentName]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {isToday(b) && (
        <span className="ml-auto rounded-full border border-brand-text px-2 text-xs font-medium text-brand-text">
          {t('intranet.home.today')}
        </span>
      )}
    </li>
  );
  let body: ReactNode;
  if (birthdays.isPending) body = <Skeleton />;
  else if (birthdays.isError)
    body = (
      <Failure what={t('intranet.home.whatBirthdays')} onRetry={() => void birthdays.refetch()} />
    );
  else if (all.length === 0)
    body = (
      <div className="flex flex-col gap-2 text-sm text-muted">
        <p>{t('intranet.home.empty.birthdaysNone')}</p>
        {can(me, 'people.private.manage') && (
          <p className="rounded-md border border-dashed border-control-border bg-paper px-3 py-2 text-ink">
            {t('intranet.home.empty.birthdaysNoneAdmin')}
          </p>
        )}
      </div>
    );
  else if (list.length === 0)
    body = (
      <p className="text-sm text-muted">
        {t('intranet.home.empty.birthdaysMonth', { month: monthName })}
      </p>
    );
  else
    body = (
      <>
        <ul className="flex flex-col">{shown.map(row)}</ul>
        <button
          type="button"
          onClick={() => setFull(true)}
          className="inline-flex items-center gap-1 self-start text-sm font-medium text-brand-text hover:underline"
        >
          {t('intranet.home.seeMonth', { count: list.length, month: monthName })}
          <ChevronRight size={14} aria-hidden />
        </button>
      </>
    );
  return (
    <Block
      icon={Cake}
      title={t('intranet.home.birthdays')}
      action={all.length > 0 ? nav : undefined}
      className="order-2 lg:order-none"
    >
      {body}
      <Modal
        open={full}
        onClose={() => setFull(false)}
        size="sm"
        title={t('intranet.home.birthdaysOf', { month: monthName })}
        description={t('intranet.home.birthdaysNote')}
        footer={
          <Button variant="secondary" onClick={() => setFull(false)}>
            {t('common.close')}
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex justify-center">{nav}</div>
          {list.length ? (
            <ul className="flex flex-col">{list.map(row)}</ul>
          ) : (
            <p className="text-sm text-muted">
              {t('intranet.home.empty.birthdaysMonth', { month: monthName })}
            </p>
          )}
        </div>
      </Modal>
    </Block>
  );
}

const CATEGORY_ORDER: IntranetLinkCategory[] = [
  'tools',
  'policies',
  'benefits',
  'documents',
  'support',
];

/** Links úteis por categoria: ferramentas, políticas, benefícios, documentos e atendimento. */
export function LinksBlock({ home }: { home: HomeQuery }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <Block icon={Link2} title={t('intranet.home.links')}>
      {byState(home, home.data?.links, {
        what: t('intranet.home.whatLinks'),
        empty: (
          <Empty
            text={t('intranet.home.empty.links')}
            publish={{ to: '/hr/intranet/links', label: t('intranet.home.publish.link') }}
          />
        ),
        content: (links) => (
          <div className="grid gap-4 sm:grid-cols-2">
            {CATEGORY_ORDER.map((c) => {
              const inCategory = links.filter((l) => l.category === c);
              if (inCategory.length === 0) return null;
              return (
                <div key={c} className="flex min-w-0 flex-col gap-1">
                  <span className="text-xs font-semibold text-muted">
                    {t(`intranet.categories.${c}`)}
                  </span>
                  {inCategory.map((l) => (
                    <DocLink key={l.id} url={l.url} label={intranetText(l.label, lang)} />
                  ))}
                </div>
              );
            })}
          </div>
        ),
      })}
    </Block>
  );
}
